import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowLeftIcon, ArrowRightIcon } from "lucide-react";
import { toast } from "sonner";
import { useLearner } from "../../contexts/LearnerContext";
import { useOnboarding } from "../../hooks/useOnboarding";
import { units } from "../../data/courses";
import { minuteOptions } from "../../data/onboarding";
import { Logo } from "../../components/ui/Logo";
import { ThemeToggle } from "../../components/ui/ThemeToggle";
import { BrutalButton } from "../../components/ui/BrutalButton";
import { PlanSoFar } from "../../components/onboarding/PlanSoFar";
import { WelcomeStep } from "../../components/onboarding/steps/WelcomeStep";
import { LanguageStep } from "../../components/onboarding/steps/LanguageStep";
import { GoalsStep } from "../../components/onboarding/steps/GoalsStep";
import { LevelStep } from "../../components/onboarding/steps/LevelStep";
import { CanDoStep } from "../../components/onboarding/steps/CanDoStep";
import { QuizStep } from "../../components/onboarding/steps/QuizStep";
import { FocusStep } from "../../components/onboarding/steps/FocusStep";
import { ScheduleStep } from "../../components/onboarding/steps/ScheduleStep";
import { PlanStep } from "../../components/onboarding/steps/PlanStep";
import { courseTitle, getLanguage } from "../../utils/language";
import { lessonPath } from "../../utils/routes";
import { selfOnlyPlacement } from "../../utils/placement";
import { spring } from "../../utils/motion";
export function Onboarding() {
  const navigate = useNavigate();
  const location = useLocation();
  const reduce = useReducedMotion() ?? false;
  const {
    user,
    addCourse,
    setDailyGoal,
    setLearningProfile
  } = useLearner();
  const ob = useOnboarding();
  const {
    answers,
    step,
    placement
  } = ob;
  const name = ((location.state as {
    name?: string;
  } | null)?.name || user.name).split(" ")[0];
  const targetName = answers.target ? getLanguage(answers.target).name : "your language";
  const showAside = step !== "welcome" && step !== "plan";
  const showFooter = step !== "welcome" && step !== "plan";

  // Enter continues, unless the learner is typing or on a button.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Enter" || !showFooter || !ob.canContinue) return;
      const tag = (e.target as HTMLElement).tagName;
      if (tag === "BUTTON" || tag === "A" || tag === "INPUT") return;
      ob.next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ob, showFooter]);
  useEffect(() => {
    window.scrollTo({
      top: 0
    });
  }, [step]);
  const finish = (dest: "lesson" | "dashboard") => {
    const target = answers.target ?? "es";
    const pair = {
      target,
      base: answers.base
    };
    // Extra languages first, main language last so it becomes the active course.
    answers.targets.slice(1).forEach(code => {
      const p = selfOnlyPlacement(answers, code);
      addCourse(code, answers.base, {
        unit: p.unit,
        level: p.level
      });
    });
    addCourse(target, answers.base, {
      unit: placement.unit,
      level: placement.level
    });
    setDailyGoal(minuteOptions.find(m => m.minutes === answers.minutes)?.xp ?? 40);
    setLearningProfile({
      goals: answers.goals,
      focus: answers.focus,
      speaking: answers.speaking,
      minutes: answers.minutes,
      reminder: answers.reminder
    });
    const extra = answers.targets.length - 1;
    toast.success(extra > 0 ? `${answers.targets.length} courses are set up` : `${courseTitle(pair)} is set up`, {
      description: "Tweak your goal and reminders any time in Settings."
    });
    const firstLesson = (units[placement.unit - 1] ?? units[0]).lessons[0];
    navigate(dest === "lesson" ? lessonPath(pair, firstLesson.id) : "/app");
  };
  const skipSetup = () => {
    toast("Setup skipped", {
      description: "Pick a course from your dashboard whenever you're ready."
    });
    navigate("/app");
  };

  // Enter-only transition: waiting on the previous step's exit could leave the panel empty.
  const slide = reduce ? {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    }
  } : {
    initial: {
      opacity: 0,
      x: 32 * ob.direction
    },
    animate: {
      opacity: 1,
      x: 0
    }
  };
  return <div className="pat-hatch flex min-h-screen w-full flex-col bg-paper font-sans text-ink" data-id="element-1395" data-mp="33jgawqwe4x27">
      <header className="mx-auto flex w-full max-w-6xl items-center gap-4 px-5 py-4 md:px-8" data-id="element-1396" data-mp="3r915y85tjkru">
        <Logo to="/" compact data-id="element-1397" data-mp="3w57zf1h133sw" />
        <div className="flex-1" data-id="element-1398" data-mp="3hh4xq4a0qe9w">
          <AnimatePresence data-id="element-1399" data-mp="3hg3jxs1mbdoh">
            {step !== "welcome" && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="mx-auto h-2 max-w-md overflow-hidden rounded-full bg-sunken" role="progressbar" aria-label="Setup progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(ob.progress)} data-id="element-1400" data-mp="3mfy8vq1u1t6w">
                <motion.div className="h-full w-full origin-left rounded-full bg-brand" initial={false} animate={{
              scaleX: ob.progress / 100
            }} transition={spring.smooth} data-id="element-1401" data-mp="1yhxfo8a8xvk5" />
              </motion.div>}
          </AnimatePresence>
        </div>
        {step !== "plan" && <button type="button" onClick={skipSetup} className="focus-ring whitespace-nowrap rounded-lg px-2 py-1.5 text-sm font-semibold text-ink-2 hover:text-ink" data-id="element-1402" data-mp="1fkmkozvndoai">
            Skip for now
          </button>}
        <ThemeToggle data-id="element-1403" data-mp="1oiptqwpg4slf" />
      </header>

      <main className={`mx-auto w-full max-w-6xl flex-1 px-5 md:px-8 ${showFooter ? "pb-32" : "pb-16"} pt-6 md:pt-10`} data-id="element-1404" data-mp="34yiz5jn64wf3">
        <div className={showAside ? "grid gap-10 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-14" : ""} data-id="element-1405" data-mp="2zzxwzeocw2o0">
          <div className={showAside ? "max-w-2xl" : ""} data-id="element-1406" data-mp="1xmjlwvw204do">
            <motion.div key={step} {...slide} transition={spring.smooth} data-id="element-1407" data-mp="1xo4ewh2pzf9f">
                {step === "welcome" && <WelcomeStep name={name} onStart={ob.next} data-id="element-1408" data-mp="22n7lw729fvk1" />}
                {step === "languages" && <LanguageStep answers={answers} update={ob.update} data-id="element-1409" data-mp="26nhpxe6hsbw9" />}
                {step === "goals" && <GoalsStep answers={answers} update={ob.update} targetName={targetName} data-id="element-1410" data-mp="2bl9kkfyiuc45" />}
                {step === "level" && <LevelStep answers={answers} update={ob.update} onDone={ob.next} targetName={targetName} data-id="element-1411" data-mp="1exd7hl2alyxo" />}
                {step === "cando" && <CanDoStep answers={answers} update={ob.update} onDone={ob.next} targetName={targetName} data-id="element-1412" data-mp="1nw70l1gf076h" />}
                {step === "quiz" && ob.quizItems && <QuizStep items={ob.quizItems} answers={answers} update={ob.update} onDone={ob.next} targetLang={answers.target ?? "es"} data-id="element-1413" data-mp="1so8cundl1xwi" />}
                {step === "focus" && <FocusStep answers={answers} update={ob.update} onTouched={ob.markFocusTouched} data-id="element-1414" data-mp="105be8juqa46m" />}
                {step === "schedule" && <ScheduleStep answers={answers} update={ob.update} placement={placement} data-id="element-1415" data-mp="smbugd3cuamq" />}
                {step === "plan" && <PlanStep answers={answers} placement={placement} onEdit={ob.goTo} onFinish={finish} data-id="element-1416" data-mp="nm7eqpa9tjxk" />}
            </motion.div>
          </div>

          {showAside && <div className="hidden lg:block" data-id="element-1417" data-mp="emsp1s1xhde4">
              <div className="sticky top-8" data-id="element-1418" data-mp="9nzarxethicw">
                <PlanSoFar answers={answers} placement={placement} data-id="element-1419" data-mp="9p0yp2gxlzc1" />
              </div>
            </div>}
        </div>
      </main>

      {showFooter && <footer className="fixed inset-x-0 bottom-0 z-30 border-t-2 border-line/40 bg-paper/90 backdrop-blur-md" data-id="element-1420" data-mp="3w3ampagy95ca">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-4 md:px-8" data-id="element-1421" data-mp="3r5sa9a3q47zj">
            <button type="button" onClick={ob.back} className="focus-ring flex items-center gap-1.5 rounded-lg px-2 py-2 text-[15px] font-semibold text-ink-2 hover:text-ink" data-id="element-1422" data-mp="3n241wp79p8d8">
              <ArrowLeftIcon className="h-4 w-4" data-id="element-1423" data-mp="19uurc0z1pnn8" /> Back
            </button>
            <BrutalButton onClick={ob.next} disabled={!ob.canContinue} size="md" data-id="element-1424" data-mp="1eufoq6ykhnxu">
              {step === "schedule" ? "Build my plan" : "Continue"} <ArrowRightIcon className="h-4 w-4" data-id="element-1425" data-mp="10vg3l9a9jr6f" />
            </BrutalButton>
          </div>
        </footer>}
    </div>;
}